import { useEffect, useMemo, useState } from 'react'
import { NetworkStatus } from '@apollo/client'

import { useTerritoryQueries } from 'hooks-queries/territory'
import { useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import type { INormalizedDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'
import { getNormalizedTerritoriesDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'
import { getTerritorySubtitleForTable } from 'utils/getTerritorySubtitleForTable'

import { GET_UNIFIED_TERRITORIES_LOG } from './use-get-unified-territories.consts'
import type { IUseGetUnifiedTerritories } from './use-get-unified-territories.types'

export default function useGetUnifiedTerritories({ variables, audience }: IUseGetUnifiedTerritories) {
  const [errorState, setErrorState] = useState(null)

  const t = useTranslate()
  const workspaceId = (PanelStore?.selectedPanel as any)?.id

  const { data, error, refetch, networkStatus, fetchMore } = useTerritoryQueries({
    queryName: 'getUnifiedTerritories',
    queryVariables: variables,
    errorLog: GET_UNIFIED_TERRITORIES_LOG,
  })

  const isLoading = [
    NetworkStatus.loading,
    NetworkStatus.fetchMore,
    NetworkStatus.refetch,
    NetworkStatus.setVariables,
  ].includes(networkStatus)

  const territoriesDataForTable: INormalizedDataForTerritoriesTable[] = useMemo(() => {
    const entities = data?.listTerritoriesPaginated?.data ?? []

    const entitiesWithUpdatedSubtitle = entities.map(item => {
      const allParents = getTerritorySubtitleForTable(item, t)
      return {
        ...item,
        allParents,
      }
    })

    const normalizedTerritoriesDataForTable = getNormalizedTerritoriesDataForTerritoriesTable(
      entitiesWithUpdatedSubtitle,
      variables?.timeRange?.timeValue,
      workspaceId,
      undefined,
      audience
    )

    const tableDataWithSearch = normalizedTerritoriesDataForTable.map(item => {
      return {
        ...item,
        highlight: variables?.filters?.title,
      }
    })

    return tableDataWithSearch
  }, [
    audience,
    data?.listTerritoriesPaginated?.data,
    t,
    variables?.filters?.title,
    variables?.timeRange?.timeValue,
    workspaceId,
  ])

  const hasMore = data?.listTerritoriesPaginated?.data?.length < data?.listTerritoriesPaginated?.totalCount

  const loadMore = async () => {
    if (isLoading || !hasMore || !data?.listTerritoriesPaginated?.cursor) return

    try {
      await fetchMore({
        variables: {
          pagination: {
            take: variables.pagination.take,
            cursor: data?.listTerritoriesPaginated?.cursor,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const result = fetchMoreResult.listTerritoriesPaginated?.data?.length

          if (!result) {
            return prev
          }

          return Object.assign({}, prev, {
            listTerritoriesPaginated: {
              ...prev.listTerritoriesPaginated,
              cursor: fetchMoreResult?.listTerritoriesPaginated?.cursor,
              data: [
                ...(prev?.listTerritoriesPaginated?.data || []),
                ...(fetchMoreResult?.listTerritoriesPaginated?.data || []),
              ],
            },
          })
        },
      })
    } catch (error) {
      console.error('searchTopics @GET_UNIFIED_TERRITORIES >>>>>>>>', error)
      setErrorState(error)
    }
  }

  const handleRefetch = () => {
    refetch()
    setErrorState(null)
  }

  useEffect(() => {
    setErrorState(undefined)
  }, [variables])

  return {
    data: territoriesDataForTable,
    totalCount: data?.listTerritoriesPaginated?.totalCount,
    isLoading: [NetworkStatus.loading, NetworkStatus.refetch, NetworkStatus.setVariables].includes(networkStatus),
    isFetchingMore: networkStatus === NetworkStatus.fetchMore,
    fetchMore: loadMore,
    error: error ?? errorState,
    refetch: handleRefetch,
    hasMore,
    wasCalled: data?.listTerritoriesPaginated,
  }
}
